// Pure helpers for the Templates screen: no React, so they are easy to test.

export const LIMITS = {
  name: 100, header: 60, body: 1024, footer: 60, buttonText: 25, buttons: 10, links: 2, calls: 1,
  copyCode: 15, cards: 10, minCards: 2, cardBody: 160, cardButtons: 2, expiry: 90,
};
export const NAME_RE = /^[a-z0-9_]{1,100}$/;

export const LANGUAGES = [
  ["en", "English"],
  ["en_US", "English (US)"],
  ["hi", "Hindi"],
];

export const CATEGORIES = [
  ["utility", "Utility", "An update about a customer's own service: a booking, a repair, a reminder."],
  ["marketing", "Marketing", "Offers and promotions. Meta checks these more strictly and charges more."],
];

export const BUTTON_KINDS = {
  QUICK_REPLY: "Quick reply",
  URL: "Link",
  PHONE_NUMBER: "Call",
  COPY_CODE: "Copy code",
};

// What a card can have: a reply, a link or a call. No copy code.
export const CARD_BUTTON_KINDS = ["QUICK_REPLY", "URL", "PHONE_NUMBER"];

export const newButton = (type = "QUICK_REPLY") => ({ type, text: "", url: "", url_example: "", phone_number: "", example: "" });

// The kinds of template Chat360 can make, with what each one is for.
export const TYPES = [
  ["text", "Text", "A message, with an optional header, footer and buttons."],
  ["image", "Image", "A picture above the message."],
  ["video", "Video", "A short video above the message."],
  ["document", "Document", "A PDF the customer can open."],
  ["location", "Location", "The message with a map pin, chosen when it is sent."],
  ["carousel", "Carousel", "Cards the customer swipes, each with a picture and buttons."],
  ["authentication", "Code", "A one-time code with a copy button."],
];
export const FILE_TYPES = ["image", "video", "document"];
export const FILE_ACCEPT = { image: ".jpg,.jpeg,.png", video: ".mp4,.3gp", document: ".pdf" };
export const FILE_HINT = { image: "A JPG or PNG, up to 5 MB.", video: "An MP4 or 3GP, up to 16 MB.", document: "A PDF, up to 20 MB." };
export const typeLabel = (type) => (TYPES.find(([v]) => v === type) || TYPES[0])[1];
export const typeHasButtons = (type) => type === "text" || FILE_TYPES.includes(type);
export const typeHasBody = (type) => type !== "authentication";

export const newCard = () => ({ media_url: "", body: "", buttons: [newButton("QUICK_REPLY")] });
export const EMPTY_OTP = { button_text: "Copy code", security_recommendation: true, code_expiration_minutes: "" };

// A file link is only ever worth checking by its ending; the server and Chat360 check the rest.
export const fileKind = (url) => {
  const end = String(url || "").split("?")[0].split("#")[0].toLowerCase();
  if (/\.(mp4|3gp)$/.test(end)) return "video";
  if (/\.pdf$/.test(end)) return "document";
  if (/\.(jpe?g|png)$/.test(end)) return "image";
  return "";
};

export const EMPTY_FORM = {
  template_type: "text",
  media_url: "", // the sample file of an image, video or document template
  cards: [], // the cards of a carousel
  otp: EMPTY_OTP, // the options of a code template
  name: "",
  use_prefix: true, // Chat360 is told the brand prefix plus the name
  dealer_id: "", // empty: the whole brand
  template_key: "", // the Service journey step this is for; empty: none yet
  field_map: {}, // which field fills each placeholder, once a step is chosen
  language: "en",
  category: "utility",
  header_text: "",
  body: "",
  examples: [],
  footer_text: "",
  buttons: [],
};

// {{1}}, {{2}} ... found in the message, once each, in order.
export const placeholderNumbers = (body) =>
  [...new Set([...String(body || "").matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map((m) => Number(m[1])))].sort((a, b) => a - b);

export const placeholderCount = (body) => placeholderNumbers(body).length;

export const nextPlaceholder = (body) => Math.max(0, ...placeholderNumbers(body)) + 1;

// Placeholders must run 1, 2, 3 with none missing; Chat360 refuses anything else.
export const hasGap = (body) => placeholderNumbers(body).some((n, i) => n !== i + 1);

// WhatsApp can refuse a message that opens or closes with a variable.
export const edgeVariable = (body) => /^\s*\{\{\s*\d+\s*\}\}|\{\{\s*\d+\s*\}\}\s*$/.test(String(body || ""));

// The message cut into plain text and variables, with each variable's example value.
export function bodySegments(body, examples = []) {
  const parts = [];
  let last = 0;
  const text = String(body || "");
  for (const m of text.matchAll(/\{\{\s*(\d+)\s*\}\}/g)) {
    if (m.index > last) parts.push({ type: "text", value: text.slice(last, m.index) });
    const n = Number(m[1]);
    parts.push({ type: "var", n, value: String(examples[n - 1] || "").trim() });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ type: "text", value: text.slice(last) });
  return parts;
}

// Puts `token` into `text` over the selection, and says where the cursor should land.
export function insertAt(text, start, end, token) {
  const s = Math.max(0, Math.min(start ?? text.length, text.length));
  const e = Math.max(s, Math.min(end ?? s, text.length));
  return { text: text.slice(0, s) + token + text.slice(e), caret: s + token.length };
}

// "auto-vista" -> "auto_vista_". Every brand shares one Chat360 number, so Chat360 is told the
// brand's prefix plus the name; the server does the same, and this only shows it up front.
export const namePrefix = (brandSlug) => {
  const slug = String(brandSlug || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return slug ? `${slug}_` : "";
};

export function nameProblem(name) {
  if (!name) return "";
  if (/[A-Z]/.test(name)) return "Use small letters only.";
  if (/\s/.test(name)) return "Use underscores in place of spaces.";
  if (!NAME_RE.test(name)) return "Only small letters, numbers and underscores.";
  return "";
}

export function relativeTime(iso, now = Date.now()) {
  if (!iso) return "Not checked yet";
  const seconds = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (seconds < 45) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

const cleanButton = (b) => {
  if (b.type === "COPY_CODE") return { type: "COPY_CODE", example: (b.example || "").trim() };
  const out = { type: b.type, text: b.text.trim() };
  if (b.type === "URL") {
    out.url = b.url.trim();
    if (out.url.includes("{{")) out.url_example = b.url_example.trim();
  }
  if (b.type === "PHONE_NUMBER") out.phone_number = b.phone_number.trim();
  return out;
};

const cleanOtp = (otp) => ({
  button_text: (otp.button_text || "").trim(),
  security_recommendation: otp.security_recommendation !== false,
  code_expiration_minutes: otp.code_expiration_minutes === "" || otp.code_expiration_minutes == null ? null : Number(otp.code_expiration_minutes),
});

const cleanCard = (c) => ({ media_url: c.media_url.trim(), body: c.body.trim(), buttons: c.buttons.map(cleanButton) });

export function toPayload(form) {
  const type = form.template_type || "text";
  const count = typeHasBody(type) ? placeholderCount(form.body) : 0;
  return {
    template_type: type,
    ...(FILE_TYPES.includes(type) ? { media_url: form.media_url.trim() } : {}),
    ...(type === "carousel" ? { cards: form.cards.map(cleanCard) } : {}),
    ...(type === "authentication" ? { otp: cleanOtp(form.otp) } : {}),
    name: form.name.trim(),
    use_prefix: form.use_prefix !== false,
    dealer_id: form.dealer_id === "" || form.dealer_id == null ? null : Number(form.dealer_id),
    ...(form.template_key ? { template_key: form.template_key, field_map: form.field_map } : {}),
    language: form.language,
    category: type === "authentication" ? "authentication" : form.category,
    body: typeHasBody(type) ? form.body : "",
    body_examples: Array.from({ length: count }, (_, i) => (form.examples[i] || "").trim()),
    header_text: type === "text" ? form.header_text : "",
    footer_text: type === "carousel" || type === "authentication" ? "" : form.footer_text,
    buttons: typeHasButtons(type) ? form.buttons.map(cleanButton) : [],
  };
}

// What a button still needs, in plain words. `label` says which button.
function buttonProblems(b, label) {
  const out = [];
  if (b.type === "COPY_CODE") {
    if (!/^[A-Za-z0-9]{1,15}$/.test((b.example || "").trim())) out.push(`Give ${label} a code of up to 15 letters and numbers`);
    return out;
  }
  if (!b.text.trim()) out.push(`Add text to ${label}`);
  if (b.type === "URL" && !b.url.trim().startsWith("https://")) out.push(`Start the link in ${label} with https://`);
  if (b.type === "URL" && b.url.includes("{{") && !b.url_example.trim()) out.push(`Add an example link to ${label}`);
  if (b.type === "PHONE_NUMBER" && !/^\+\d{8,15}$/.test(b.phone_number.trim())) out.push(`Give ${label} a phone number like +919876543210`);
  return out;
}

function cardProblems(cards) {
  const out = [];
  if (cards.length < LIMITS.minCards) out.push(`Add at least ${LIMITS.minCards} cards`);
  cards.forEach((c, i) => {
    const label = `card ${i + 1}`;
    if (!c.media_url.trim()) out.push(`Add a file to ${label}`);
    if (c.body.includes("{{")) out.push(`Take the variable out of ${label}`);
    if (c.buttons.length < 1) out.push(`Give ${label} a button`);
    c.buttons.forEach((b, j) => out.push(...buttonProblems(b, `button ${j + 1} of ${label}`)));
  });
  const shape = (c) => c.buttons.map((b) => b.type).join();
  if (cards.length && cards.some((c) => shape(c) !== shape(cards[0]))) out.push("Every card needs the same kinds of buttons, in the same order");
  if (cards.length && cards.some((c) => !!c.body.trim() !== !!cards[0].body.trim())) out.push("Either every card has text, or none does");
  return out;
}

// What still stops each step, in plain words: [basics, message, buttons]. Empty means ready.
export function stepProblems(form) {
  const type = form.template_type || "text";
  const basics = [];
  if (!form.name.trim()) basics.push("Give it a name");
  else if (nameProblem(form.name.trim())) basics.push("Fix the name");

  const message = [];
  if (type === "authentication") {
    const minutes = form.otp.code_expiration_minutes;
    if (minutes !== "" && minutes != null && !(Number.isInteger(Number(minutes)) && Number(minutes) >= 1 && Number(minutes) <= LIMITS.expiry)) {
      message.push(`The code expiry is 1 to ${LIMITS.expiry} minutes`);
    }
    if (!(form.otp.button_text || "").trim()) message.push("Give the button some text");
  } else {
    if (!form.body.trim()) message.push("Write the message");
    if (hasGap(form.body)) message.push("Number the variables 1, 2, 3 with none missing");
    placeholderNumbers(form.body).forEach((n) => {
      if (!(form.examples[n - 1] || "").trim()) message.push(`Add an example for {{${n}}}`);
    });
    if (new Set(placeholderNumbers(form.body).map((n) => (form.examples[n - 1] || "").trim())).size < placeholderCount(form.body)) {
      message.push("Give each variable a different example");
    }
  }
  if (FILE_TYPES.includes(type) && !form.media_url.trim()) message.push("Add the sample file");
  if (type === "carousel") message.push(...cardProblems(form.cards));

  const buttons = [];
  if (typeHasButtons(type)) form.buttons.forEach((b, i) => buttons.push(...buttonProblems(b, `button ${i + 1}`)));
  const quick = form.buttons.map((b, i) => (b.type === "QUICK_REPLY" ? i : -1)).filter((i) => i >= 0);
  if (typeHasButtons(type) && quick.length && quick[quick.length - 1] - quick[0] + 1 !== quick.length) buttons.push("Keep the quick replies together");
  return [basics, message, buttons];
}

// Every problem on every step. Empty means the template can be sent.
export const missing = (form) => stepProblems(form).flat();

// How many more buttons of this kind the template can take.
export function buttonRoom(buttons, kind) {
  if (buttons.length >= LIMITS.buttons) return 0;
  if (kind === "URL") return Math.max(0, LIMITS.links - buttons.filter((b) => b.type === "URL").length);
  if (kind === "PHONE_NUMBER") return Math.max(0, LIMITS.calls - buttons.filter((b) => b.type === "PHONE_NUMBER").length);
  if (kind === "COPY_CODE") return buttons.some((b) => b.type === "COPY_CODE") ? 0 : 1;
  return LIMITS.buttons - buttons.length;
}

// A saved template turned back into form values, to send again, or (duplicate) to start a new
// one from it under a new name.
export function formFromRow(row, duplicate = false) {
  const redo = !duplicate && (row.status === "rejected" || row.status === "failed");
  const type = TYPES.some(([v]) => v === row.template_type) ? row.template_type : "text";
  const otp = row.otp || {};
  return {
    ...EMPTY_FORM,
    template_type: type,
    media_url: row.media_url || "",
    cards: (row.cards || []).map((c) => ({ media_url: c.media_url || "", body: c.body || "", buttons: (c.buttons || []).map((b) => ({ ...newButton(b.type), ...b, text: b.text || "" })) })),
    otp: { button_text: otp.button_text || EMPTY_OTP.button_text, security_recommendation: otp.security_recommendation !== false, code_expiration_minutes: otp.code_expiration_minutes ?? "" },
    name: redo ? row.name : `${row.name}_2`,
    use_prefix: redo ? Boolean(row.chat360_name) && row.chat360_name !== row.name : true,
    dealer_id: row.dealer_id ?? "",
    language: row.language || "en",
    category: ["marketing", "authentication"].includes(String(row.category || "").toLowerCase()) ? String(row.category).toLowerCase() : "utility",
    header_text: row.header_text || "",
    body: row.body || "",
    examples: [...(row.body_examples || [])],
    footer_text: row.footer_text || "",
    buttons: (row.buttons || []).map((b) => ({ ...newButton(b.type), ...b, text: b.text || "" })),
  };
}

// Ready-made starting points. Each fills the whole form; everything stays editable.
export function ideas(brandName) {
  const team = `${brandName || "Service"} Team`;
  return [
    {
      key: "complaint",
      label: "Complaint received",
      hint: "Tells the customer their complaint is logged",
      form: {
        ...EMPTY_FORM,
        name: "complaint_received",
        header_text: "Complaint registered",
        body:
          "Hello {{1}}, we have registered your complaint {{2}} for vehicle {{3}} at {{4}}. Our service manager will contact you shortly. Thank you for your patience.",
        examples: ["Ravi", "C-1024", "MH12AB3131", "Andheri"],
        footer_text: team,
      },
    },
    {
      key: "reminder",
      label: "Service reminder",
      hint: "Asks the customer to book the next service",
      form: {
        ...EMPTY_FORM,
        name: "service_reminder",
        body: "Hello {{1}}, your {{2}} is due for service on {{3}}. Reply to this message to book a convenient slot.",
        examples: ["Ravi", "Swift", "20 October"],
        footer_text: team,
        buttons: [{ ...newButton("QUICK_REPLY"), text: "Book a slot" }],
      },
    },
    {
      key: "ready",
      label: "Vehicle ready",
      hint: "Lets the customer know it is time to collect",
      form: {
        ...EMPTY_FORM,
        name: "vehicle_ready",
        body: "Hello {{1}}, your vehicle {{2}} is ready for delivery at {{3}}. Please collect it before {{4}}.",
        examples: ["Ravi", "MH12AB3131", "Andheri", "6 PM today"],
        footer_text: team,
      },
    },
  ];
}
